import React, { memo, useCallback } from "react";
import { Text, HStack, Spacer, VStack, IconButton } from "@chakra-ui/react";
import UserAvatar from "modules/_shared/user-avatar";
import { MessageThreadFragment, UserFragment } from "gql/graphql";
import UserMention from "modules/_shared/mentions/user-mention";
import {
  getUserFullName,
  getUserOutOfOfficeNote,
  isUserInactive,
  isUserOutOfOffice,
  isUserSmsOnlyAndNotLoggedIn,
} from "modules/utils/Functions";
import colors from "app/colors";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import {
  BellOutlineSlashIcon,
  PhoneSmsIcon,
  RemovePersonIcon,
} from "modules/icons";
import {
  MODAL_TYPE,
  useOverlay,
} from "modules/providers/overlay-provider/overlay-provider";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  messageThread: MessageThreadFragment;
  user: UserFragment;
}

const MessageThreadProfileDrawerUser = ({ messageThread, user }: IProps) => {
  const { me } = useAuth();
  const { openModal } = useOverlay();

  const onRemoveUserClicked = useCallback(
    (userId: string) => () => {
      openModal({
        type: MODAL_TYPE.CONFIRM_REMOVE_USERS_GROUPS,
        id: messageThread.id,
        ids: [userId],
      });
    },
    [messageThread, openModal],
  );

  const { getCanRemoveUser } = useRemovedFromThread(messageThread);
  const canRemoveUser = getCanRemoveUser(user.id);

  return (
    <HStack w="full">
      <UserAvatar userId={user.id} size="sm" />
      <VStack spacing={0} alignItems="flex-start">
        <HStack spacing={1}>
          <UserMention id={user.id} />
          {isUserSmsOnlyAndNotLoggedIn(user) && (
            <Text fontSize={12} color={colors.grayMain} fontWeight={500}>
              (via SMS)
            </Text>
          )}
          {isUserInactive(user) && (
            <Text fontSize={12} color={colors.grayMain} fontWeight={500}>
              (Inactive)
            </Text>
          )}
          {isUserOutOfOffice(user) && (
            <Text fontSize={12} fontWeight={500} color={colors.grayMain}>
              ({getUserOutOfOfficeNote(user.outOfOffice)})
            </Text>
          )}
        </HStack>
        <Text fontSize={12} color={colors.gray50} fontWeight={400}>
          {getUserFullName(user)}
        </Text>
      </VStack>
      <Spacer />
      {!messageThread.notificationUserIdList.includes(user.id) && (
        <SyncoTooltip label="Notifications muted for conversation">
          <BellOutlineSlashIcon boxSize={5} fill={colors.gray30} />
        </SyncoTooltip>
      )}
      {isUserSmsOnlyAndNotLoggedIn(user) && (
        <SyncoTooltip label="SMS Only User">
          <PhoneSmsIcon boxSize={5} fill={colors.gray30} />
        </SyncoTooltip>
      )}
      {canRemoveUser && (
        <SyncoTooltip
          label={me?.id === user.id ? "Leave Thread" : "Remove from Thread"}
        >
          <IconButton
            icon={<RemovePersonIcon fill={colors.blue} />}
            aria-label="remove user"
            variant="ghost"
            onClick={onRemoveUserClicked(user.id)}
          />
        </SyncoTooltip>
      )}
    </HStack>
  );
};

export default memo(MessageThreadProfileDrawerUser);
